import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useScriptCreation, TextSegmentData, getSimplifiedMeditationParts } from '../../contexts/ScriptCreationContext';
import { useCoins, useUser } from '../../contexts/UserContext';
import { Icon } from '../../components/common/Icon';
import { FaCog, FaChevronUp, FaChevronDown, FaEllipsisV, FaPlay, FaStop, FaMagic, FaMicrophone, FaEdit, FaCompactDisc, FaTimes, FaArrowRight, FaForward } from 'react-icons/fa';
import { config } from '../../config';
import { useTTSQueue } from '../../hooks/useTTSQueue';
import { authService } from '../../services/auth';
import { coinService } from '../../services/coinService';
import { detectSupportedAudioFormat, convertAudioBufferToMP3, blobToAudioBuffer } from '../../utils/audioConversion';
import { parseTextToSegments as parseTextToSegmentsUtil, getPauseTimeForPunctuation as getPauseTimeForPunctuationUtil } from '../../utils/parseTextToSegments';
import { HelpButton } from '../../components/common/HelpButton';

const helpContent = `
<div class="alert info">
   Generate speech for your meditation script.
   </br></br>
<ul>
<li>First select a voice that you like, you can preview the voice with the play button next to the voice name.</li>
<li>Click the gear icon to adjust the speech speed, and pause timings for the entire script.</li>
<li>Click the three dots on a segment to see options to play the segment, generate speech, or adjust the speech speed for the segment.</li>
<li>Adjust individual pause timings by clicking and dragging the pause bubble.</li>
<li>"Re-use Speech" will save you coins by using cached speech, if available for the voice, speech speed and text.</li>
<li>Click the blue "Generate" button to generate speech for the entire script part.</li>
<li>Press the purple "Play" button to play the speech for the entire script part.</li>
<li>Part names can be edited by clicking on the part name and typing.</li>
<li>Press the "Finalize Audio" button once you have listened to all script parts and are happy with speech and pause timings, to merge all speech parts and pauses into a single audio file for further editing.</li>
</div>

`;

const Container = styled.div`
  //max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
`;

const VoiceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background-color: #333;
  border-radius: 8px;
  border: 1px solid #444;
`;

const VoiceInfoText = styled.span`
  color: #ffffff;
  font-size: 14px;
`;

const PlaySampleButton = styled.button`
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(0, 123, 255, 0.1);
  }

  &:disabled {
    color: #666;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 10px 15px;
  border: 1px solid #444;
  border-radius: 8px;
  background-color: #2a2a2a;
  color: #ffffff;
  font-size: 14px;
  min-width: 120px;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const GearButton = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 20px;
  cursor: pointer;
  padding: 10px;
  border-radius: 8px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #333;
  }
`;

const FinalizeButton = styled.button`
  background: linear-gradient(135deg, #4caf50, #45a049);
  border: none;
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  box-shadow: 0 2px 4px rgba(76, 175, 80, 0.3);

  &:hover {
    background: linear-gradient(135deg, #45a049, #3d8b40);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(76, 175, 80, 0.4);
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
    opacity: 0.6;
  }
`;

const ModalBody = styled.div`
  color: #ccc;
  line-height: 1.6;
  margin-bottom: 20px;
`;

const ProgressContainer = styled.div`
  margin: 20px 0;
`;

const ProgressText = styled.div`
  color: #4caf50;
  font-weight: 600;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SuccessMessage = styled.div`
  color: #4caf50;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
`;

const ErrorMessage = styled.div`
  color: #f44336;
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 6px;
  padding: 12px;
  margin-bottom: 15px;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const PlayFinalButton = styled.button`
  background: linear-gradient(135deg, #2196f3, #1976d2);
  border: none;
  color: white;
  padding: 10px 16px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s;

  &:hover {
    background: linear-gradient(135deg, #1976d2, #1565c0);
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ContinueButton = styled.button`
  background: linear-gradient(135deg, #4caf50, #45a049);
  border: none;
  color: white;
  padding: 12px 20px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  &:hover {
    background: linear-gradient(135deg, #45a049, #3d8b40);
    transform: translateY(-1px);
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ContentBox = styled.div`
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 12px;
  padding: 20px;
`;

const ContentHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
`;

const ContentTitleWithControls = styled.div`
  display: flex;
  align-items: center;
`;

const ReorderControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-right: 10px;
`;

const ReorderButton = styled.button`
  background-color: #444;
  color: #ffffff;
  border: none;
  padding: 4px 6px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover:not(:disabled) {
    background-color: #555;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ContentTitleInput = styled.input`
  color: #ffffff;
  background: transparent;
  border: none;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  font-family: inherit;
  outline: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  min-width: 200px;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  &:focus {
    background-color: rgba(255, 255, 255, 0.1);
    border: 1px solid #007bff;
  }
`;

const CollapseButton = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 18px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;

  &:hover {
    background-color: #444;
  }
`;

const PartActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  margin-right: 10px;
`;

const GenerateButton = styled.button.attrs({ type: 'button' })`
  background-color: #007bff;
  color: #ffffff;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #555;
    cursor: not-allowed;
  }
`;

const ReuseSpeechButton = styled.button`
  background-color: #28a745;
  color: #ffffff;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background-color: #1e7e34;
  }

  &:disabled {
    background-color: #555;
    cursor: not-allowed;
  }
`;

const PlayButton = styled.button`
  background-color: #6f42c1;
  color: #ffffff;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background-color: #5a32a3;
  }

  &:disabled {
    background-color: #555;
    cursor: not-allowed;
  }
`;

const StopButton = styled.button`
  background-color: #dc3545;
  color: #ffffff;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover {
    background-color: #c82333;
  }

  &:disabled {
    background-color: #555;
    cursor: not-allowed;
  }
`;

const SegmentDisplay = styled.div`
  padding: 15px;
  border: 1px solid #444;
  border-radius: 8px;
  background-color: #1a1a1a;
  min-height: 120px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  align-content: flex-start;
`;

const TextSegmentWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const TextSegment = styled.span<{ $hasMenu?: boolean }>`
  display: inline-block;
  background-color: #333;
  padding: 4px 8px;
  padding-right: ${props => props.$hasMenu ? '28px' : '8px'};
  margin: 2px;
  border-radius: 16px;
  color: #ffffff;
  font-size: 14px;
  border: 1px solid #444;
  cursor: pointer;
  outline: none;

  &:hover {
    background-color: #444;
  }

  &:focus {
    background-color: #555;
    border-color: #007bff;
  }

  @keyframes pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(23, 162, 184, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(23, 162, 184, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(23, 162, 184, 0);
    }
  }

  @keyframes playbackPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.7);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(40, 167, 69, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
    }
  }

  &.generating {
    animation: pulse 2s infinite;
  }

  &.playing {
    animation: playbackPulse 2s infinite;
  }
`;

const MenuButton = styled.button.attrs({ type: 'button' })`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 2px;
  border-radius: 2px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  
  &:hover {
    opacity: 1;
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MenuDropdown = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  min-width: 150px;
  display: ${props => props.$isOpen ? 'block' : 'none'};
  margin-top: 4px;
`;

const MenuItem = styled.button.attrs({ type: 'button' })<{ $isUnderlined?: boolean; $underlineColor?: string }>`
  width: 100%;
  background: none;
  border: none;
  color: #ffffff;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  text-decoration: ${props => props.$isUnderlined ? 'underline' : 'none'};
  text-decoration-color: ${props => props.$underlineColor || 'transparent'};
  text-underline-offset: 2px;
  
  &:hover {
    background-color: #333;
  }
  
  &:first-child {
    border-radius: 8px 8px 0 0;
  }
  
  &:last-child {
    border-radius: 0 0 8px 8px;
  }
  
  &:only-child {
    border-radius: 8px;
  }
`;

const PauseBubble = styled.div`
  display: inline-block;
  background-color: #28a745;
  color: white;
  padding: 4px 8px;
  margin: 2px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  min-width: 30px;
  text-align: center;
  border: 1px solid #20a040;
  position: relative;
  user-select: none;

  &:hover {
    background-color: #218838;
  }
`;

const DragHandle = styled.div`
  position: absolute;
  right: -3px;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 20px;
  background-color: #ffffff;
  border-radius: 4px;
  cursor: ew-resize;
  opacity: 0;
  transition: opacity 0.2s;
  border: 1px solid #ccc;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);

  ${PauseBubble}:hover & {
    opacity: 0.9;
  }

  &:hover {
    opacity: 1;
    background-color: #f8f8f8;
    border-color: #999;
  }

  &:active {
    background-color: #e0e0e0;
    border-color: #666;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  color: #666;
  font-size: 16px;
  padding: 40px;
`;

const PlaceholderText = styled.div`
  color: #666;
  font-style: italic;
  text-align: center;
  padding: 20px;
`;

// Queue status components
const QueueStatusBar = styled.div`
  background-color: #2a2a2a;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const QueueInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const QueueStat = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #cccccc;
`;

const QueueDot = styled.div<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${props => props.$color};
`;

const ConnectionStatus = styled.div<{ $connected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: ${props => props.$connected ? '#28a745' : '#dc3545'};
`;


const FinalizeSpeechButton = styled.button`
  background-color: #28a745;
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-top: 30px;

  &:hover {
    background-color: #218838;
  }

  &:disabled {
    background-color: #666;
    cursor: not-allowed;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
`;

// Modal components
const Modal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: #2a2a2a;
  padding: 30px;
  border-radius: 12px;
  max-width: 500px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const ModalTitle = styled.h2`
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  font-size: 24px;
  cursor: pointer;
`;

const SettingGroup = styled.div`
  margin-bottom: 20px;
`;

const SettingLabel = styled.label`
  display: block;
  color: #ffffff;
  margin-bottom: 8px;
  font-weight: 600;
`;

const SettingInput = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #444;
  border-radius: 6px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 14px;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const SettingTextArea = styled.textarea`
  width: 100%;
  padding: 8px;
  border: 1px solid #444;
  border-radius: 6px;
  background-color: #1a1a1a;
  color: #ffffff;
  font-size: 14px;
  min-height: 60px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

// Meditation parts data (simplified version)
// Using centralized meditation parts from ScriptCreationContext
const meditationParts = getSimplifiedMeditationParts();

const Speech: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const { refreshCoins } = useCoins();
  const {
    scriptId,
    scriptName,
    isEditingExisting,
    selectedParts,
    customPartNames,
    collapsedParts,
    contentData,
    setContentData,
    editingContent,
    setEditingContent,
    availableVoices,
    selectedVoice,
    selectedVoiceData,
    selectedLanguage,
    segmentationMode,
    advancedSettings,
    setAdvancedSettings,
    openMenuSegmentId,
    setOpenMenuSegmentId,
    dragState,
    setDragState,
    togglePartCollapse,
    handlePartNameChange,
    getPartDisplayName,
    movePartUp,
    movePartDown,
    loadVoices,
    handleVoiceChange,
    playSampleAudio,
    playPartAudio,
    playPartAudioFromSegment,
    stopPartAudio,
    isPlayingAudio,
    currentPlayingPart,
    currentPlayingSegment,
    saveSegmentsAndUpdatePartText,
    scriptDbId,
    partDbIds,
    markSegmentCompleted,
    markSegmentError,
    finalizeScriptAudio,
    isFinalizingAudio,
    finalizedAudioFile,
    finalizationError,
    segmentsToText
  } = useScriptCreation();
  
  // Use ref to access current contentData without causing callback changes
  const contentDataRef = useRef(contentData);
  const savedPartsRef = useRef<Set<string>>(new Set());
  const lastProcessedStateRef = useRef<string>('');
  useEffect(() => {
    contentDataRef.current = contentData;
  }, [contentData]);

  // Memoize callbacks to prevent reconnection cycles - stable references
  const onSegmentCompleted = useCallback((segmentId: string, partId: string, audioFile: string, duration?: number, ttsCacheId?: string) => {
    console.log(`Speech: TTS completed for segment ${segmentId} in part ${partId} with audio:`, audioFile, 'ttsCacheId:', ttsCacheId);
    markSegmentCompleted(partId, segmentId, audioFile, ttsCacheId);
    refreshCoins();
  }, [markSegmentCompleted, refreshCoins]);

  const onSegmentError = useCallback((segmentId: string, partId: string, error: string) => {
    console.log(`Speech: TTS error for segment ${segmentId} in part ${partId}:`, error);
    markSegmentError(partId, segmentId, error);
  }, [markSegmentError]);

  const {
    queueSegment,
    queueBatch,
    getSegmentStatus,
    getPartQueueItems,
    queueItems,
    globalQueueLength,
    isConnected
  } = useTTSQueue({
    onSegmentCompleted,
    onSegmentError
  });

  const [showAdvancedModal, setShowAdvancedModal] = useState(false);

  // Custom prompt modal state
  const [showCustomPromptModal, setShowCustomPromptModal] = useState(false);
  const [customPromptPartId, setCustomPromptPartId] = useState<string>('');
  const [customPromptSegmentId, setCustomPromptSegmentId] = useState<string>('');
  const [customPromptValue, setCustomPromptValue] = useState<string>('');

  // Speech speed modal state
  const [showSpeechSpeedModal, setShowSpeechSpeedModal] = useState(false);
  const [speechSpeedPartId, setSpeechSpeedPartId] = useState<string>('');
  const [speechSpeedSegmentId, setSpeechSpeedSegmentId] = useState<string>('');
  const [speechSpeedValue, setSpeechSpeedValue] = useState<number>(1.0);

  // Voice recording modal state
  const [showVoiceModal, setShowVoiceModal] = useState(false);
  const [voiceModalPartId, setVoiceModalPartId] = useState<string>('');
  const [voiceModalSegmentId, setVoiceModalSegmentId] = useState<string>('');
  const [voiceModalText, setVoiceModalText] = useState<string>('');
  const [isRecording, setIsRecording] = useState(false);
  const [recordedAudio, setRecordedAudio] = useState<Blob | null>(null);
  const [recordedAudioUrl, setRecordedAudioUrl] = useState<string>('');
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [isSavingAudio, setIsSavingAudio] = useState(false);

  // Finalize audio modal state
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);

  // No voice selected modal state
  const [showNoVoiceModal, setShowNoVoiceModal] = useState(false);

  // Coin-related state
  const [showInsufficientCoinsModal, setShowInsufficientCoinsModal] = useState(false);
  const [coinErrorDetails, setCoinErrorDetails] = useState<{
    required: number;
    available: number;
    shortfall: number;
    context?: string;
  } | null>(null);

  // Track if voices have been loaded to prevent multiple loads
  const voicesLoadedRef = useRef(false);
  
  // Load voices when component mounts and user is available
  useEffect(() => {
    console.log('Speech useEffect for voice loading triggered, user:', !!user, 'already loaded:', voicesLoadedRef.current);
    if (user && !voicesLoadedRef.current) {
      console.log('Calling loadVoices from Speech component');
      voicesLoadedRef.current = true;
      loadVoices();
      
    }
  }, [user]); // Only depend on user, not loadVoices

  // Initialize the voice when voices are loaded and none is validly selected.
  // Prefer the user's default voice (profile_settings.default_voice_id), else the first.
  useEffect(() => {
    if (availableVoices.length === 0) return;
    const hasValidSelection = !!selectedVoice &&
      availableVoices.some((v) => v.id === selectedVoice || v.voiceId === selectedVoice);
    if (hasValidSelection) return;

    const defaultId = user?.profile_settings?.default_voice_id;
    const defaultVoice = defaultId ? availableVoices.find((v) => v.id === defaultId) : null;
    const toSelect = defaultVoice || availableVoices[0];
    if (toSelect) {
      handleVoiceChange(toSelect.id);
    }
  }, [availableVoices, selectedVoice, user]); // Omit handleVoiceChange to prevent loops

  // Only parse content to segments when absolutely necessary (no segments exist but text does)
  useEffect(() => {
    console.log('🔄 Speech page useEffect triggered');
    console.log('selectedParts:', selectedParts);
    console.log('editingContent:', editingContent);
    console.log('contentData keys:', Object.keys(contentData));
    console.log('contentData content sample:', Object.keys(contentData).map(key => ({
      [key]: contentData[key]?.length || 0
    })));
    
    // Create a simple key based on just the inputs we care about for triggering parsing
    // We'll check for actual changes in content vs segments below
    // Note: Don't sort selectedParts - it should maintain the order from Plan page
    const inputStateKey = JSON.stringify({
      selectedParts: selectedParts,
      editingContentText: selectedParts.map(partId => editingContent[partId] || '').join('|')
    });
    
    console.log('🔑 Input state key:', inputStateKey);
    console.log('🔑 Last processed input key:', lastProcessedStateRef.current);
    
    // Only skip if we've processed this exact input combination recently
    if (inputStateKey === lastProcessedStateRef.current) {
      console.log('⏭️ Skipping processing - same input state as last time');
      return;
    }
    
    let needsUpdate = false;
    
    selectedParts.forEach(partId => {
      const hasSegments = contentData[partId] && contentData[partId].length > 0;
      const hasText = editingContent[partId] && editingContent[partId].trim().length > 0;
      const currentText = editingContent[partId] || '';
      
      console.log(`🔍 Part ${partId} debug:`, {
        hasSegments,
        hasText,
        contentDataExists: !!contentData[partId],
        contentDataLength: contentData[partId]?.length || 0,
        contentDataSample: contentData[partId]?.slice(0, 2) || 'none'
      });
      
      // Check if text was actually edited by comparing with what segments would produce
      let shouldParseText = false;
      
      if (hasSegments) {
        // We have existing segments - only parse if text was manually edited and differs
        const segmentsGeneratedText = segmentsToText(contentData[partId]);
        const textDiffers = currentText.trim() !== segmentsGeneratedText.trim();
        
        if (textDiffers) {
          console.log(`Part ${partId}: Text was edited on Write page, will reparse`);
          console.log(`Current text: "${currentText}"`);
          console.log(`Generated from segments: "${segmentsGeneratedText}"`);
          shouldParseText = true;
        }
      } else if (hasText) {
        // No segments exist at all - this is a new part from Write page
        console.log(`Part ${partId}: No segments exist, will parse new text`);
        shouldParseText = true;
      }
      
      if (shouldParseText) {
        needsUpdate = true;
      }
    });
    
    if (needsUpdate) {
      console.log('Speech page: Need to parse content (no existing segments found)');
      const newContentData = { ...contentData };
      const partsToSave: string[] = [];
      
      selectedParts.forEach(partId => {
        const hasSegments = contentData[partId] && contentData[partId].length > 0;
        const hasText = editingContent[partId] && editingContent[partId].trim().length > 0;
        const currentText = editingContent[partId] || '';
        
        // Check if we should parse text using the same logic
        let shouldParseText = false;
        
        if (hasSegments) {
          // We have existing segments - only parse if text was manually edited and differs
          const segmentsGeneratedText = segmentsToText(contentData[partId]);
          const textDiffers = currentText.trim() !== segmentsGeneratedText.trim();
          
          if (textDiffers) {
            console.log(`Part ${partId}: Text was edited on Write page, will reparse`);
            shouldParseText = true;
          }
        } else if (hasText) {
          // No segments exist at all - this is a new part from Write page
          console.log(`Part ${partId}: No segments exist, will parse new text`);
          shouldParseText = true;
        }

        if (shouldParseText) {
          // Make each segment end with a paragraph break 
          const textToParse = currentText ;//+ '\n\n[2s] '; end paragraph break will be added in parseTextToSegments
          console.log(`Parsing text for part ${partId}:`, textToParse);
          const segments = parseTextToSegments(textToParse, partId, segmentationMode);
          console.log(`Generated segments for ${partId}:`, segments);
          newContentData[partId] = segments;
          partsToSave.push(partId);
        }
      });
      
      console.log('Updating contentData with new segments');
      setContentData(newContentData);
      
      // Save segments to database if they don't have database IDs
      if (partsToSave.length > 0 && scriptDbId) {
        setTimeout(() => {
          console.log('Checking and saving segments to database for parts:', partsToSave);
          partsToSave.forEach(partId => {
            if (newContentData[partId]) {
              // Check if segments need to be saved
              const textSegments = newContentData[partId].filter(seg => seg.type === 'text' && seg.content.trim());
              
              if (textSegments.length > 0 && !savedPartsRef.current.has(partId)) {
                console.log(`Saving segments for part ${partId}...`);
                savedPartsRef.current.add(partId);
                const segmentsToSave = newContentData[partId].map(seg => ({
                  ...seg,
                  isNew: true,
                  hasChanged: false
                }));
                saveSegmentsAndUpdatePartText(partId, segmentsToSave);
              } else {
                console.log(`Segments for part ${partId} already have database IDs or already attempted save, skipping`);
              }
            }
          });
        }, 100);
      }
    }
    
    // Update the processed state key to prevent reprocessing the same input
    lastProcessedStateRef.current = inputStateKey;
  }, [selectedParts, editingContent, contentData, segmentsToText, advancedSettings.paragraphPause]); // Include all dependencies to prevent stale closures

  // Check if existing segments need to be saved to database on initial load only
  const hasInitializedSave = useRef(false);
  useEffect(() => {
    if (scriptDbId && Object.keys(contentData).length > 0 && !hasInitializedSave.current) {
      hasInitializedSave.current = true;
      const timer = setTimeout(() => {
        Object.keys(contentData).forEach(partId => {
          const segments = contentData[partId];
          if (segments && segments.length > 0) {
            // Check if segments need to be saved
            const textSegments = segments.filter(seg => seg.type === 'text' && seg.content.trim());
            
            if (textSegments.length > 0 && !savedPartsRef.current.has(partId)) {
              console.log(`Saving segments for part ${partId}...`);
              savedPartsRef.current.add(partId);
              const segmentsToSave = segments.map(seg => ({
                ...seg,
                isNew: true,
                hasChanged: false
              }));
              saveSegmentsAndUpdatePartText(partId, segmentsToSave);
            }
          }
        });
      }, 200); // Slight delay to avoid conflicts with the parsing save
      
      return () => clearTimeout(timer);
    }
  }, [scriptDbId, contentData]); // Add contentData back but gate with hasInitializedSave

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuSegmentId(null);
    };
    
    if (openMenuSegmentId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openMenuSegmentId]);

  // Parse individual segment text without adding paragraph breaks (for segment editing)
  const parseSegmentText = (text: string, partId: string = 'default', mode: 'punctuation' | 'sentence' | 'paragraph' = segmentationMode): TextSegmentData[] => {
    const segments: TextSegmentData[] = [];
    let currentId = 0;
    
    console.log('Parsing segment text:', JSON.stringify(text));
    
    // Clean up text
    const cleanedText = text
      .replace(/\u00A0/g, ' ')     // Replace non-breaking spaces with regular spaces
      .replace(/\u200B/g, '')      // Remove zero-width spaces
      .trim();                     // Remove leading/trailing whitespace
    
    if (!cleanedText) return segments;
    
    // Split based on segmentation mode, but treat as single paragraph (no paragraph break logic)
    let parts: string[];
    if (mode === 'sentence') {
      // Split by sentence-ending punctuation OR pause notation [number], keeping the delimiters
      parts = cleanedText.split(/(\s*[.!?]\s*|\[\d*\.?\d+[sm]?\])/);
    } else {
      // Default punctuation mode: Split by all punctuation marks OR pause notation [number]
      parts = cleanedText.split(/(\s*[.!?,:;-]\s*|\[\d*\.?\d+[sm]?\])/);
    }
    
    console.log(`Segment parts (${mode} mode):`, parts);
    
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i].trim();
      if (!part) continue;
      
      // Check if this is a pause notation [number] with optional s/m unit
      const pauseMatch = part.match(/^\[(\d*\.?\d+)([sm])?\]$/);
      if (pauseMatch) {
        const value = parseFloat(pauseMatch[1]);
        const unit = pauseMatch[2] || 's'; // default to seconds if no unit
        const pauseTime = unit === 'm' ? `${value * 60}s` : `${value}s`;
        segments.push({
          id: `${partId}-segment-${currentId++}`,
          type: 'pause',
          content: part,
          pauseTime: pauseTime,
          isManuallyModified: true,
          isParagraphBreak: false
        });
      } else if (mode === 'sentence' ? /[.!?]/.test(part) : /[.!?,:;-]/.test(part)) {
        // This is punctuation - create a pause bubble with default timing
        const punctuationMatch = mode === 'sentence' 
          ? part.match(/[.!?]/) 
          : part.match(/[.!?,:;-]/);
        const punctuationMark = punctuationMatch ? punctuationMatch[0] : part;
        const pauseTime = getPauseTimeForPunctuation(punctuationMark);
        segments.push({
          id: `${partId}-segment-${currentId++}`,
          type: 'pause',
          content: punctuationMark,
          pauseTime: pauseTime,
          isManuallyModified: false,
          isParagraphBreak: false
        });
      } else {
        // This is text - create one segment for the entire text block
        segments.push({
          id: `${partId}-segment-${currentId++}`,
          type: 'text',
          content: part,
          pauseTime: '',
          isManuallyModified: false,
          isParagraphBreak: false
        });
      }
    }
    
    return segments;
  };

  // Parse text to segments \u2014 delegates to the shared util so the Zen flow and
  // the Speech page produce identical segmentation.
  const parseTextToSegments = (text: string, partId: string = 'default', mode: 'punctuation' | 'sentence' | 'paragraph' = segmentationMode): TextSegmentData[] => {
    return parseTextToSegmentsUtil(text, partId, mode, advancedSettings);
  };

  const getPauseTimeForPunctuation = (punctuation: string): string => {
    return getPauseTimeForPunctuationUtil(punctuation, advancedSettings);
  };

  const validatePauseTime = (value: string): boolean => {
    return /^\d*\.?\d+[ms]?$/.test(value.trim());
  };

  const formatPauseTimeForDisplay = (pauseTime: string, isManuallyModified: boolean, originalContent: string, segmentId?: string): string => {
    // If this segment is being dragged, use the current drag value
    if (segmentId && dragState?.isDragging && dragState.segmentId === segmentId && (dragState as any)?.currentValue !== undefined) {
      const dragValue = (dragState as any).currentValue;
      if (dragValue >= 60 && dragValue % 60 === 0) {
        return `${dragValue / 60}m`;
      } else if (dragValue >= 60) {
        return `${Math.round((dragValue / 60) * 10) / 10}m`;
      } else {
        return `${dragValue}s`;
      }
    }
    if (!isManuallyModified) {
      return pauseTime;
    }
    
    if (originalContent && originalContent.match(/^\[(\d*\.?\d+)([sm])?\]$/)) {
      const match = originalContent.match(/^\[(\d*\.?\d+)([sm])?\]$/);
      if (match) {
        const value = match[1];
        const unit = match[2] || 's';
        return `${value}${unit}`;
      }
    }
    
    const seconds = parseFloat(pauseTime.replace(/[^\d.]/g, '') || '0');
    if (seconds >= 60 && seconds % 60 === 0) {
      return `${seconds / 60}m`;
    } else if (seconds >= 60) {
      return `${Math.round((seconds / 60) * 10) / 10}m`;
    } else {
      return pauseTime;
    }
  };

  const handlePauseTimeChange = (partId: string, segmentId: string, newPauseTime: string) => {
    if (!validatePauseTime(newPauseTime)) return;
    
    const updatedSegments = contentData[partId]?.map(segment => {
      if (segment.id === segmentId) {
        const wasOriginallyParagraphFormat = segment.content.match(/^\[(\d*\.?\d+)([sm])?\]$/);
        const wasParagraphBreak = segment.content === '\n';
        
        if (wasOriginallyParagraphFormat || wasParagraphBreak) {
          const pauseValue = parseFloat(newPauseTime.replace(/[^\d.]/g, '') || '0');
          let newContent;
          if (pauseValue >= 60 && pauseValue % 60 === 0) {
            newContent = `[${pauseValue / 60}m]`;
          } else if (pauseValue >= 60) {
            newContent = `[${Math.round((pauseValue / 60) * 10) / 10}m]`;
          } else {
            newContent = `[${pauseValue}s]`;
          }
          
          return { 
             ...segment, 
             pauseTime: newPauseTime, 
             isManuallyModified: true,
             content: wasParagraphBreak ? '\n' : newContent,
             isParagraphBreak: true
           };
        } else {
          return { ...segment, pauseTime: newPauseTime, isManuallyModified: true };
        }
      }
      return segment;
    }) || [];
    
    // Use dual-save to update both segments and part text
    saveSegmentsAndUpdatePartText(partId, updatedSegments);
  };

  // Convert segments back to text representation using [number] notation for pauses (from original NewScript.tsx)
  // This function is currently unused but kept for future reference
  /*
  const segmentsToText = (segments: TextSegmentData[]): string => {
    let text = '';
    
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const prevSegment = i > 0 ? segments[i - 1] : null;
      const nextSegment = i < segments.length - 1 ? segments[i + 1] : null;
      
      if (segment.type === 'text') {
        text += segment.content;
      } else if (segment.type === 'pause') {
        if (segment.content === '\n' && !segment.isManuallyModified) {
          // Keep default paragraph breaks as double newlines
          text += '\n\n';
        } else if (segment.isManuallyModified && (segment.content === '\n' || segment.content.match(/^\[(\d*\.?\d+)([sm])?\]$/))) {
          // Use the isParagraphBreak flag for consistent detection
          const isParagraphBreak = segment.isParagraphBreak || segment.content === '\n';
          
          if (isParagraphBreak) {
            // This is a paragraph break - use the original format if available
            let formattedPause;
            if (segment.content.match(/^\[(\d*\.?\d+)([sm])?\]$/)) {
              formattedPause = segment.content; // Use original format like [5m]
            } else {
              // Fallback for old \n format
              const pauseValue = parseFloat(segment.pauseTime?.replace(/[^\d.]/g, '') || '0');
              if (pauseValue >= 60 && pauseValue % 60 === 0) {
                formattedPause = `[${pauseValue / 60}m]`;
              } else if (pauseValue >= 60) {
                formattedPause = `[${Math.round((pauseValue / 60) * 10) / 10}m]`;
              } else {
                formattedPause = `[${pauseValue}s]`;
              }
            }
            
            // Always ensure paragraph breaks are on their own lines with proper spacing
            text += `\n\n${formattedPause}\n\n`;
          } else {
            // This is an inline pause - handle it like other inline pauses
            const needsSpaceBefore = prevSegment && prevSegment.type === 'text' && 
              !prevSegment.content.endsWith(' ') && !prevSegment.content.endsWith('\n');
            const needsSpaceAfter = nextSegment && nextSegment.type === 'text' && 
              !nextSegment.content.startsWith(' ') && !nextSegment.content.startsWith('\n');
            
            if (needsSpaceBefore) text += ' ';
            text += segment.content; // Use the original format
            if (needsSpaceAfter) text += ' ';
          }
        } else if (segment.isManuallyModified && segment.content.match(/^\[(\d*\.?\d+)([sm])?\]$/)) {
          // For manually modified pauses with stored original format, use the original format
          const needsSpaceBefore = prevSegment && prevSegment.type === 'text' && 
            !prevSegment.content.endsWith(' ') && !prevSegment.content.endsWith('\n');
          const needsSpaceAfter = nextSegment && nextSegment.type === 'text' && 
            !nextSegment.content.startsWith(' ') && !nextSegment.content.startsWith('\n');
          
          // Add space before if needed
          if (needsSpaceBefore) {
            text += ' ';
          }
          
          text += segment.content; // Use the original format
          
          // Add space after if needed
          if (needsSpaceAfter) {
            text += ' ';
          }
        } else if (segment.isManuallyModified) {
          // Handle regular punctuation pauses with [number] notation
          // Try to preserve original format if available in content
          let formattedPause;
          if (segment.content && segment.content.match(/^\[(\d*\.?\d+)([sm])?\]$/)) {
            // Use the original format from content
            formattedPause = segment.content;
          } else {
            // Fallback: format based on pause time
            const pauseValue = parseFloat(segment.pauseTime?.replace(/[^\d.]/g, '') || '0');
            if (pauseValue >= 60 && pauseValue % 60 === 0) {
              formattedPause = `[${pauseValue / 60}m]`;
            } else if (pauseValue >= 60) {
              formattedPause = `[${Math.round((pauseValue / 60) * 10) / 10}m]`;
            } else {
              formattedPause = `[${pauseValue}s]`;
            }
          }
          
          const needsSpaceBefore = prevSegment && prevSegment.type === 'text' && 
            !prevSegment.content.endsWith(' ') && !prevSegment.content.endsWith('\n');
          const needsSpaceAfter = nextSegment && nextSegment.type === 'text' && 
            !nextSegment.content.startsWith(' ') && !nextSegment.content.startsWith('\n');
          
          // Add space before if needed
          if (needsSpaceBefore) {
            text += ' ';
          }
          
          text += formattedPause;
          
          // Add space after if needed
          if (needsSpaceAfter) {
            text += ' ';
          }
        } else {
          // Keep original punctuation for default pauses and add space after only
          const needsSpaceAfter = nextSegment && nextSegment.type === 'text' && 
            !nextSegment.content.startsWith(' ') && !nextSegment.content.startsWith('\n');
          
          text += segment.content;
          if (needsSpaceAfter) text += ' ';
        }
      }
    }
    
    return text;
  };
  */

  const handleDragStart = (partId: string, segmentId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    const segment = contentData[partId]?.find(s => s.id === segmentId);
    if (!segment) return;
    
    const currentValue = parseFloat(segment.pauseTime?.replace(/[^\d.]/g, '') || '0');
    
    const newDragState = {
      isDragging: true,
      partId,
      segmentId,
      startX: e.clientX,
      startValue: currentValue,
      currentValue: currentValue // Add temporary value for display during drag
    };
    
    setDragState(newDragState);
    
    const handleMouseMove = (event: MouseEvent) => {
      event.preventDefault();
      
      const deltaX = event.clientX - newDragState.startX;
      const currentValue = newDragState.startValue;
      let sensitivity = 0.01;
      if (currentValue >= 60) {
        sensitivity = 0.1;
      } else if (currentValue >= 10) {
        sensitivity = 0.05;
      }
      const newValue = Math.max(0, newDragState.startValue + (deltaX * sensitivity));
      const roundedValue = Math.round(newValue * 10) / 10;
      
      // Only update the visual state during drag, don't regenerate text
      setDragState({
        ...newDragState,
        currentValue: roundedValue
      });
    };
    
    const handleMouseUp = async (event: MouseEvent) => {
      event.preventDefault();
      
      // Calculate the final value properly based on mouse position
      const deltaX = event.clientX - newDragState.startX;
      const currentValue = newDragState.startValue;
      let sensitivity = 0.01;
      if (currentValue >= 60) {
        sensitivity = 0.1;
      } else if (currentValue >= 10) {
        sensitivity = 0.05;
      }
      const newValue = Math.max(0, newDragState.startValue + (deltaX * sensitivity));
      const finalValue = Math.round(newValue * 10) / 10;
      
      // If dragged to zero, handle pause deletion
      if (finalValue === 0) {
        const segments = contentData[newDragState.partId] || [];
        const segmentIndex = segments.findIndex(s => s.id === newDragState.segmentId);
        const segment = segments[segmentIndex];
        
        if (segment && segment.type === 'pause') {
          const prevSegment = segmentIndex > 0 ? segments[segmentIndex - 1] : null;
          const nextSegment = segmentIndex < segments.length - 1 ? segments[segmentIndex + 1] : null;
          
          // If there are text segments on both sides of the pause, merge them
          if (prevSegment && nextSegment && prevSegment.type === 'text' && nextSegment.type === 'text') {
            // No need to delete individual segments - they're stored as JSON in the part now
            
            // Merge the text segments with a space
            const mergedContent = prevSegment.content.trim() + ' ' + nextSegment.content.trim();
            
            const updatedSegments = segments.map((seg, idx) => {
              if (idx === segmentIndex - 1) {
                // Update the previous segment with merged content
                return { 
                  ...seg, 
                  content: mergedContent,
                  // Inherit properties from next segment if previous doesn't have them
                  customPrompt: seg.customPrompt || nextSegment.customPrompt,
                  inputAudioFile: seg.inputAudioFile || nextSegment.inputAudioFile,
                  hasChanged: true
                };
              }
              return seg;
            }).filter(seg => seg.id !== newDragState.segmentId && seg.id !== nextSegment.id); // Remove both pause and next segment
            
            // Use dual-save to update both segments and part text
            saveSegmentsAndUpdatePartText(newDragState.partId, updatedSegments);
          } else {
            // No text segments to merge, just remove the pause
            const updatedSegments = segments.filter(seg => seg.id !== newDragState.segmentId);
            // Use dual-save to update both segments and part text
            saveSegmentsAndUpdatePartText(newDragState.partId, updatedSegments);
          }
        }
      } else {
        handlePauseTimeChange(newDragState.partId, newDragState.segmentId, `${finalValue}s`);
      }
      
      setDragState(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const handleMenuToggle = (partId: string, segmentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const uniqueMenuId = `${partId}:${segmentId}`;
    setOpenMenuSegmentId(openMenuSegmentId === uniqueMenuId ? null : uniqueMenuId);
  };

  const handleMenuAction = async (action: string, partId: string, segmentId: string, segmentContent: string) => {
    setOpenMenuSegmentId(null);
    
    // Handle part-level actions (when segmentId is empty)
    if (!segmentId) {
      switch (action) {
        case 'play':
          console.log('Play entire part:', partId);
          try {
            await playPartAudio(partId);
          } catch (error) {
            console.error('Error playing part audio:', error);
          }
          break;
        case 'stop':
          console.log('Stop part audio');
          stopPartAudio();
          break;
        case 'generate':
          
          // Get all text segments in the part
          const textSegments = contentData[partId]?.filter(s => 
            s.type === 'text'
          ) || [];
          
          if (textSegments.length === 0) {
            console.log('No text segments to generate');
            return;
          }
          
          if (!selectedVoiceData) {
            setShowNoVoiceModal(true);
            return;
          }

          // Check if all segments are completed (to determine if this is a re-generate all action)
          const queuedItems = getPartQueueItems(partId);
          const queueCompleted = queuedItems.filter(item => item.status === 'completed').length;
          const savedCompleted = textSegments.filter(s => s.generationStatus === 'completed').length;
          const totalCompleted = Math.max(queueCompleted, savedCompleted);
          const allCompleted = totalCompleted === textSegments.length && textSegments.length > 0;

          let segmentsToGenerate;
          
          if (allCompleted) {
            // Re-generate all segments
            segmentsToGenerate = textSegments;
          } else {
            // Only generate segments that haven't been completed
            segmentsToGenerate = textSegments.filter(seg => {
              const queueStatus = getSegmentStatus(seg.id);
              // Don't generate if already completed or currently processing/queued
              return !(
                seg.generationStatus === 'completed' || 
                (queueStatus && (queueStatus.status === 'completed' || queueStatus.status === 'queued' || queueStatus.status === 'processing'))
              );
            });
          }

          if (segmentsToGenerate.length === 0) {
            return;
          }
          
          try {
            // Check coins before batch generation
            const totalCost = segmentsToGenerate.reduce((sum, seg) => sum + seg.content.length, 0);
            const coinCheck = await coinService.checkTTSCost(totalCost);
            
            if (!coinCheck.hasEnoughCoins) {
              setCoinErrorDetails({
                required: coinCheck.cost,
                available: coinCheck.currentBalance,
                shortfall: coinCheck.shortfall,
                context: `batch generation (${segmentsToGenerate.length} segments)`
              });
              setShowInsufficientCoinsModal(true);
              return;
            }
            
            // Use batch queue for better performance
            await queueBatch(
              segmentsToGenerate.map(seg => ({
                segmentId: seg.id,
                scriptId: scriptDbId,
                partId: partDbIds[partId],
                text: seg.content,
                speed: seg.speechSpeed || advancedSettings.speechSpeed,
                inputAudioFile: seg.inputAudioFile || undefined
              })),
              selectedVoiceData.voiceId
            );
          } catch (error) {
            console.error('Error queuing batch generation:', error);
            
            // Check if it's a coin-related error
            if (coinService.isCoinError(error)) {
              const errorDetails = coinService.parseCoinError(error);
              if (errorDetails && errorDetails.required && errorDetails.available) {
                setCoinErrorDetails({
                  required: errorDetails.required,
                  available: errorDetails.available,
                  shortfall: errorDetails.shortfall || (errorDetails.required - errorDetails.available),
                  context: `batch generation (${segmentsToGenerate.length} segments)`
                });
                setShowInsufficientCoinsModal(true);
                return;
              }
            }
          }
          break;
        case 'reuse':
          console.log('Re-use speech for entire part:', partId);
          
          // Get all text segments in the part
          const reuseTextSegments = contentData[partId]?.filter(s => 
            s.type === 'text'
          ) || [];
          
          if (reuseTextSegments.length === 0) {
            console.log('No text segments to reuse');
            return;
          }
          
          if (!selectedVoiceData) {
            setShowNoVoiceModal(true);
            return;
          }

          // Try to reuse cached speech for each segment
          const reusePromises = reuseTextSegments.map(async (segment) => {
            try {
              const result = await coinService.reuseCachedSpeech({
                segmentId: segment.id,
                scriptId: scriptDbId,
                partId: partDbIds[partId],
                text: segment.content,
                voiceId: selectedVoiceData.voiceId,
                language: selectedLanguage,
                speed: segment.speechSpeed || advancedSettings.speechSpeed,
                inputAudioFile: segment.inputAudioFile || undefined
              });
              
              console.log(`Successfully reused cached audio for segment ${segment.id}`);
              return { 
                segmentId: segment.id, 
                success: true, 
                audioFile: result.audioFile,
                cacheId: result.cacheId,
                message: result.message 
              };
            } catch (error) {
              console.log(`No cached audio found for segment ${segment.id}:`, error);
              return { segmentId: segment.id, success: false, error: error };
            }
          });
          
          // Execute all reuse attempts
          try {
            const results = await Promise.all(reusePromises);
            const successful = results.filter(r => r.success);
            const failed = results.filter(r => !r.success);
            
            // Batch update all successful segments at once
            if (successful.length > 0) {
              const updatedData = { ...contentDataRef.current };
              
              // Update all successful segments in a single pass
              for (const pid in updatedData) {
                const segments = updatedData[pid];
                updatedData[pid] = segments.map((seg: TextSegmentData) => {
                  const successResult = successful.find(r => r.segmentId === seg.id);
                  if (successResult && successResult.audioFile) {
                    return {
                      ...seg,
                      generatedAudioFile: successResult.audioFile,
                      ttsCacheId: successResult.cacheId || seg.ttsCacheId,
                      generationStatus: 'completed' as const,
                      generationError: null,
                      hasChanged: false
                    };
                  }
                  return seg;
                });
              }
              
              // Single state update with all changes
              setContentData(updatedData);
              console.log(`Successfully reused cached audio for ${successful.length} segment(s)`);
            }
            
            if (failed.length > 0) {
              console.log(`No cached audio found for ${failed.length} segment(s)`);
            }
          } catch (error) {
            console.error('Error during batch reuse:', error);
          }
          break;
      }
      return;
    }
    
    // Handle segment-level actions
    switch (action) {
      case 'play':
        console.log('Play segment:', segmentContent);
        const segmentToPlay = contentData[partId]?.find(s => s.id === segmentId);
        const queueStatus = getSegmentStatus(segmentId);
        
        // Check queue status first
        if (queueStatus?.status === 'completed' && queueStatus.audioFile) {
          const baseUrl = config.apiUrl;
          const audioUrl = queueStatus.audioFile.startsWith('/audio/') 
            ? `${baseUrl}${queueStatus.audioFile}`
            : queueStatus.audioFile;
          
          console.log('Playing audio from queue result:', audioUrl);
          const audio = new Audio(audioUrl);
          audio.play().catch(error => {
            console.error('Error playing generated audio:', error);
          });
        } else if (segmentToPlay?.generatedAudioFile) {
          // Fallback to saved audio file
          const baseUrl = config.apiUrl;
          const audioUrl = segmentToPlay.generatedAudioFile.startsWith('/audio/') 
            ? `${baseUrl}${segmentToPlay.generatedAudioFile}`
            : segmentToPlay.generatedAudioFile;
          
          console.log('Playing audio from saved file:', audioUrl);
          const audio = new Audio(audioUrl);
          audio.play().catch(error => {
            console.error('Error playing generated audio:', error);
          });
        } else {
          console.log('No generated audio available for segment:', segmentId);
        }
        break;
      case 'playfrom':
        console.log('Play from segment:', segmentId);
        try {
          await playPartAudioFromSegment(partId, segmentId);
        } catch (error) {
          console.error('Error playing from segment:', error);
        }
        break;
      case 'generate':
        const segment = contentData[partId]?.find(s => s.id === segmentId);
        if (!segment || segment.type !== 'text') return;
        
        // Check if already in queue
        const existingQueueItem = getSegmentStatus(segmentId);
        if (existingQueueItem && 
            (existingQueueItem.status === 'queued' || existingQueueItem.status === 'processing')) {
          console.log('Segment already in queue:', segmentId);
          return;
        }
        
        if (!selectedVoiceData) {
          setShowNoVoiceModal(true);
          return;
        }
        
        console.log('Queueing segment for generation:', segmentContent);
        
        try {
          // Check coins before generation
          const coinCost = segment.content.length;
          const coinCheck = await coinService.checkTTSCost(coinCost);
          
          if (!coinCheck.hasEnoughCoins) {
            setCoinErrorDetails({
              required: coinCheck.cost,
              available: coinCheck.currentBalance,
              shortfall: coinCheck.shortfall,
              context: 'single segment'
            });
            setShowInsufficientCoinsModal(true);
            return;
          }
          
          await queueSegment({
            segmentId: segment.id,
            scriptId: scriptDbId,
            partId: partDbIds[partId],
            text: segment.content,
            speed: segment.speechSpeed || advancedSettings.speechSpeed,
            inputAudioFile: segment.inputAudioFile || undefined
          }, selectedVoiceData.voiceId);
        } catch (error) {
          console.error('Error queueing segment:', error);
          
          // Check if it's a coin-related error
          if (coinService.isCoinError(error)) {
            const errorDetails = coinService.parseCoinError(error);
            if (errorDetails && errorDetails.required && errorDetails.available) {
              setCoinErrorDetails({
                required: errorDetails.required,
                available: errorDetails.available,
                shortfall: errorDetails.shortfall || (errorDetails.required - errorDetails.available),
                context: 'single segment'
              });
              setShowInsufficientCoinsModal(true);
              return;
            }
          }
        }
        break;
      case 'reuse':
        const reuseSegment = contentData[partId]?.find(s => s.id === segmentId);
        if (!reuseSegment || reuseSegment.type !== 'text') return;
        
        if (!selectedVoiceData) {
          setShowNoVoiceModal(true);
          return;
        }
        
        console.log('Attempting to reuse cached speech for segment:', segmentContent);
        
        try {
          const result = await coinService.reuseCachedSpeech({
            segmentId: reuseSegment.id,
            scriptId: scriptDbId,
            partId: partDbIds[partId],
            text: reuseSegment.content,
            voiceId: selectedVoiceData.voiceId,
            language: selectedLanguage,
            speed: reuseSegment.speechSpeed || advancedSettings.speechSpeed,
            inputAudioFile: reuseSegment.inputAudioFile || undefined
          });
          
          // Update contentData with cached audio
          const updatedData = { ...contentDataRef.current };
          for (const pid in updatedData) {
            const segments = updatedData[pid];
            const segmentIndex = segments.findIndex((s: TextSegmentData) => s.id === reuseSegment.id);
            
            if (segmentIndex !== -1) {
              updatedData[pid] = segments.map((seg: TextSegmentData, idx: number) => 
                idx === segmentIndex ? {
                  ...seg,
                  generatedAudioFile: result.audioFile,
                  ttsCacheId: result.cacheId || seg.ttsCacheId,
                  generationStatus: 'completed' as const,
                  generationError: null,
                  hasChanged: false
                } : seg
              );
              break;
            }
          }
          setContentData(updatedData);
          
          console.log(`Successfully reused cached audio for segment ${reuseSegment.id}`);
        } catch (error) {
          console.log(`No cached audio found for segment ${segmentId}:`, error);
          // Could show a user-friendly message here if desired
        }
        break;
      case 'voice':
        // Open voice recording modal
        openVoiceModal(partId, segmentId, segmentContent);
        break;
      case 'prompt':
        // Open custom prompt modal
        openCustomPromptModal(partId, segmentId);
        break;
      case 'speed':
        // Open speech speed modal
        openSpeechSpeedModal(partId, segmentId);
        break;
    }
  };

  // Custom prompt modal functions
  const openCustomPromptModal = (partId: string, segmentId: string) => {
    const segment = contentData[partId]?.find(s => s.id === segmentId);
    if (!segment || segment.type !== 'text') return;
    
    setCustomPromptPartId(partId);
    setCustomPromptSegmentId(segmentId);
    // Pre-fill with existing custom prompt or default to advanced settings prompt
    setCustomPromptValue(segment.customPrompt || '');
    setShowCustomPromptModal(true);
  };

  const closeCustomPromptModal = () => {
    setShowCustomPromptModal(false);
    setCustomPromptPartId('');
    setCustomPromptSegmentId('');
    setCustomPromptValue('');
  };

  // Speech speed modal functions
  const openSpeechSpeedModal = (partId: string, segmentId: string) => {
    const segment = contentData[partId]?.find(s => s.id === segmentId);
    if (!segment || segment.type !== 'text') return;
    
    setSpeechSpeedPartId(partId);
    setSpeechSpeedSegmentId(segmentId);
    // Pre-fill with existing speed or default to advanced settings speed
    setSpeechSpeedValue(segment.speechSpeed || advancedSettings.speechSpeed);
    setShowSpeechSpeedModal(true);
  };

  const closeSpeechSpeedModal = () => {
    setShowSpeechSpeedModal(false);
    setSpeechSpeedPartId('');
    setSpeechSpeedSegmentId('');
    setSpeechSpeedValue(1.0);
  };

  // Voice recording modal functions
  const openVoiceModal = (partId: string, segmentId: string, segmentText: string) => {
    const segment = contentData[partId]?.find(s => s.id === segmentId);
    if (!segment || segment.type !== 'text') return;
    
    setVoiceModalPartId(partId);
    setVoiceModalSegmentId(segmentId);
    setVoiceModalText(segmentText);
    setShowVoiceModal(true);
    
    // Check if segment already has input audio file
    if (segment.inputAudioFile) {
      // If segment has existing input audio, we could load it here if needed
    }
  };

  const closeVoiceModal = () => {
    setShowVoiceModal(false);
    setVoiceModalPartId('');
    setVoiceModalSegmentId('');
    setVoiceModalText('');
    setRecordedAudio(null);
    setRecordedAudioUrl('');
    setIsRecording(false);
    setIsSavingAudio(false);
    
    // Clean up media recorder
    if (mediaRecorder) {
      mediaRecorder.stop();
      setMediaRecorder(null);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Try different audio formats in order of preference
      let mimeType = 'audio/webm';
      if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4';
      } else if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm';
      }
      
      console.log('Using audio format:', mimeType);
      const recorder = new MediaRecorder(stream, { mimeType });
      
      const audioChunks: Blob[] = [];
      
      recorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };
      
      recorder.onstop = () => {
        const audioBlob = new Blob(audioChunks, { type: mimeType });
        setRecordedAudio(audioBlob);
        setRecordedAudioUrl(URL.createObjectURL(audioBlob));
        
        // Clean up stream
        stream.getTracks().forEach(track => track.stop());
      };
      
      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      alert('Could not access microphone. Please check your permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const playRecording = () => {
    if (recordedAudioUrl) {
      const audio = new Audio(recordedAudioUrl);
      audio.play().catch(error => {
        console.error('Error playing audio:', error);
      });
    }
  };

  const removeRecording = () => {
    setRecordedAudio(null);
    setRecordedAudioUrl('');
  };

  const acceptRecording = async () => {
    if (!recordedAudio || !voiceModalPartId || !voiceModalSegmentId) return;
    
    setIsSavingAudio(true);
    try {
      console.log('Original audio blob:', { type: recordedAudio.type, size: recordedAudio.size });
      
      // Check if MP3 encoding is supported
      const audioFormat = await detectSupportedAudioFormat();
      if (audioFormat === 'unsupported') {
        throw new Error('Your browser does not support audio encoding. Please try Chrome or Edge.');
      }
      
      // Convert to MP3 client-side
      console.log('Converting audio to MP3...');
      
      // Create audio context for conversion
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      
      // Convert blob to AudioBuffer
      const audioBuffer = await blobToAudioBuffer(recordedAudio, audioContext);
      console.log('Audio buffer created:', { duration: audioBuffer.duration, sampleRate: audioBuffer.sampleRate });
      
      // Convert AudioBuffer to MP3
      const mp3Blob = await convertAudioBufferToMP3(audioBuffer, (progress) => {
        console.log(`MP3 encoding progress: ${progress}%`);
      });
      
      console.log('MP3 conversion complete:', { size: mp3Blob.size, type: mp3Blob.type });
      
      // Clean up audio context
      audioContext.close();
      
      // Create FormData for file upload with MP3
      const formData = new FormData();
      formData.append('audio', mp3Blob, `voice_${voiceModalSegmentId}_${Date.now()}.mp3`);
      formData.append('partId', voiceModalPartId);
      formData.append('segmentId', voiceModalSegmentId);
      formData.append('segmentText', voiceModalText);
      
      // Get the segment (currently unused but may be needed for future validation)
      // const segment = contentData[voiceModalPartId]?.find(s => s.id === voiceModalSegmentId);
      
      // Upload audio
      const { authService } = await import('../../services/auth');
      const token = authService.getToken();
      
      const response = await fetch(`${config.apiUrl}/upload-voice-audio`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Failed to upload audio (${response.status})`);
      }
      
      const data = await response.json();
      
      // Update local state with audio file path using dual-save
      const partSegments = contentData[voiceModalPartId] || [];
      
      const updatedSegments = partSegments.map(seg => {
        if (seg.id === voiceModalSegmentId && seg.type === 'text') {
          return {
            ...seg,
            inputAudioFile: data.audioFile,
            customPrompt: null, // Clear custom prompt when voice audio is set
            // Reset generation fields when voice audio is set
            generationStatus: 'idle' as const,
            generatedAudioFile: null,
            generationError: null,
            hasChanged: true
          };
        }
        return seg;
      });
      
      // Use dual-save to update both segments and part text
      saveSegmentsAndUpdatePartText(voiceModalPartId, updatedSegments);
      
      closeVoiceModal();
    } catch (error) {
      console.error('Error saving audio:', error);
      alert('Failed to save audio. Please try again.');
    } finally {
      setIsSavingAudio(false);
    }
  };

  const removeVoiceAudio = async () => {
    if (!voiceModalPartId || !voiceModalSegmentId) return;
    
    setIsSavingAudio(true);
    try {
      // const segment = contentData[voiceModalPartId]?.find(s => s.id === voiceModalSegmentId);
      // Mark segment as changed - will be saved when saveSegmentsAndUpdatePartText is called
      
      // Update local state to remove audio file using dual-save
      const partSegments = contentData[voiceModalPartId] || [];
      
      const updatedSegments = partSegments.map(seg => {
        if (seg.id === voiceModalSegmentId && seg.type === 'text') {
          return {
            ...seg,
            inputAudioFile: undefined,
            // Reset generation fields when voice audio is removed
            generationStatus: 'idle' as const,
            generatedAudioFile: null,
            generationError: null,
            hasChanged: true
          };
        }
        return seg;
      });
      
      // Use dual-save to update both segments and part text
      saveSegmentsAndUpdatePartText(voiceModalPartId, updatedSegments);
      
      closeVoiceModal();
    } catch (error) {
      console.error('Error removing audio:', error);
      alert('Failed to remove audio. Please try again.');
    } finally {
      setIsSavingAudio(false);
    }
  };

  const handleSaveCustomPrompt = async () => {
    if (!customPromptPartId || !customPromptSegmentId) return;
    
    const updatedContentData = { ...contentData };
    const partSegments = updatedContentData[customPromptPartId] || [];
    
    // Find the segment and get its database ID
    const segment = partSegments.find(s => s.id === customPromptSegmentId && s.type === 'text');
    if (!segment) return;
    
    const trimmedPrompt = customPromptValue.trim();
    
    // If custom prompt is empty, remove it
    const shouldRemovePrompt = !trimmedPrompt;
    const finalPrompt = shouldRemovePrompt ? null : trimmedPrompt;
    
    try {
      // Mark segment as changed - will be saved when saveSegmentsAndUpdatePartText is called
      segment.generationStatus = 'idle' as const;
      segment.generatedAudioFile = null;
      segment.generationError = null;
      segment.hasChanged = true;
      console.log('Updated segment with custom prompt, marked for save', segment);
      
      // Update local state and use dual-save
      const updatedSegments = partSegments.map(seg => {
        if (seg.id === customPromptSegmentId && seg.type === 'text') {
          return {
            ...seg,
            customPrompt: finalPrompt,
            // Reset generation fields when custom prompt is changed
            generationStatus: 'idle' as const,
            generatedAudioFile: null,
            generationError: null,
            hasChanged: true, // Mark as changed for future saves
            
          };
          
        }

        

        return seg;
      });


      
      // Use dual-save to update both segments and part text
      saveSegmentsAndUpdatePartText(customPromptPartId, updatedSegments);
      
      closeCustomPromptModal();
    } catch (error: any) {
      console.error('Error saving custom prompt:', error);
      
      // Check if it's a 404 error (segment not found or access denied)
      if (error?.response?.status === 404) {
        console.warn('Segment not found in database, updating local state only');
        
        // Still update local state even if database update failed
        const updatedSegments = partSegments.map(seg => {
          if (seg.id === customPromptSegmentId && seg.type === 'text') {
            return {
              ...seg,
              customPrompt: finalPrompt,
              // Reset generation fields when custom prompt is changed
              generationStatus: 'idle' as const,
              generatedAudioFile: null,
              generationError: null,
              hasChanged: true
            };
          }
          return seg;
        });
        
        // Use dual-save to update both segments and part text
        saveSegmentsAndUpdatePartText(customPromptPartId, updatedSegments);
        closeCustomPromptModal();
      } else {
        alert('Failed to save custom prompt. Please try again.');
      }
    }
  };

  const handleSaveSpeechSpeed = async () => {
    if (!speechSpeedPartId || !speechSpeedSegmentId) return;
    
    const updatedContentData = { ...contentData };
    const partSegments = updatedContentData[speechSpeedPartId] || [];
    
    // Find the segment and get its database ID
    const segment = partSegments.find(s => s.id === speechSpeedSegmentId && s.type === 'text');
    if (!segment) return;
    
    // If speed equals the default advanced settings speed, remove it
    const shouldRemoveSpeed = speechSpeedValue === advancedSettings.speechSpeed;
    const finalSpeed = shouldRemoveSpeed ? undefined : speechSpeedValue;
    
    try {
      // Mark segment as changed - will be saved when saveSegmentsAndUpdatePartText is called
      segment.generationStatus = 'idle' as const;
      segment.generatedAudioFile = null;
      segment.generationError = null;
      segment.hasChanged = true;
      console.log('Updated segment with speech speed, marked for save', segment);
      
      // Update local state and use dual-save
      const updatedSegments = partSegments.map(seg => {
        if (seg.id === speechSpeedSegmentId && seg.type === 'text') {
          return {
            ...seg,
            speechSpeed: finalSpeed,
            // Reset generation fields when speech speed is changed
            generationStatus: 'idle' as const,
            generatedAudioFile: null,
            generationError: null,
            hasChanged: true
          };
        }
        return seg;
      });

      // Use dual-save to update both segments and part text
      saveSegmentsAndUpdatePartText(speechSpeedPartId, updatedSegments);
      
      closeSpeechSpeedModal();
    } catch (error: any) {
      console.error('Error saving speech speed:', error);
      
      // Check if it's a 404 error (segment not found or access denied)
      if (error?.response?.status === 404) {
        console.warn('Segment not found in database, updating local state only');
        
        // Still update local state even if database update failed
        const updatedSegments = partSegments.map(seg => {
          if (seg.id === speechSpeedSegmentId && seg.type === 'text') {
            return {
              ...seg,
              speechSpeed: finalSpeed,
              // Reset generation fields when speech speed is changed
              generationStatus: 'idle' as const,
              generatedAudioFile: null,
              generationError: null,
              hasChanged: true
            };
          }
          return seg;
        });
        
        // Use dual-save to update both segments and part text
        saveSegmentsAndUpdatePartText(speechSpeedPartId, updatedSegments);
        closeSpeechSpeedModal();
      } else {
        alert('Failed to save speech speed. Please try again.');
      }
    }
  };

  const handleTextSegmentChange = async (partId: string, segmentId: string, newText: string) => {
    const currentSegments = contentData[partId] || [];
    console.log(`handleTextSegmentChange for ${partId}: currentSegments contains ${currentSegments.length} segments:`, currentSegments.map(s => ({ id: s.id, type: s.type, content: s.content })));
    const segmentIndex = currentSegments.findIndex(s => s.id === segmentId);
    
    if (segmentIndex === -1) return;
    
    const originalSegment = currentSegments[segmentIndex];
    
    // Check if text actually changed - if not, do nothing
    if (originalSegment.content === newText) {
      console.log('Text unchanged, skipping update');
      return;
    }
    
    console.log('Text changed from:', JSON.stringify(originalSegment.content), 'to:', JSON.stringify(newText));
    console.log('Resetting audio/TTS fields for segment:', segmentId);
    
    // If the text is empty, remove the segment
    if (!newText.trim()) {
      // No need to delete individual segments - they're stored as JSON in the part now
      
      const updatedSegments = currentSegments.filter(s => s.id !== segmentId);
      // Use dual-save to update both segments and part text
      saveSegmentsAndUpdatePartText(partId, updatedSegments);
      return;
    }
    
    // Check if the new text contains punctuation that should create pauses
    const containsPunctuation = /[.!?,:;-]/.test(newText);
    
    if (containsPunctuation) {
      // No need to delete individual segments - they're stored as JSON in the part now
      
      // Re-parse this text segment to handle new punctuation (without adding paragraph breaks)
      const newSegments = parseSegmentText(newText, partId, segmentationMode);
      
      // Replace the current segment with the new parsed segments
      const beforeSegments = currentSegments.slice(0, segmentIndex);
      const afterSegments = currentSegments.slice(segmentIndex + 1);
      
      console.log(`Before segments (${beforeSegments.length}):`, beforeSegments.map(s => ({ id: s.id, type: s.type, content: s.content })));
      console.log(`New parsed segments (${newSegments.length}):`, newSegments.map(s => ({ id: s.id, type: s.type, content: s.content })));
      console.log(`After segments (${afterSegments.length}):`, afterSegments.map(s => ({ id: s.id, type: s.type, content: s.content })));
      
      // Regenerate IDs to avoid conflicts
      let currentId = Math.max(0, ...currentSegments.map(s => {
        const match = s.id.match(/segment-(\d+)/);
        return match ? parseInt(match[1]) : 0;
      })) + 1;
      
      // When text changes and causes punctuation split, reset audio/TTS fields
      let firstTextSegmentProcessed = false;
      const updatedNewSegments = newSegments.map((segment) => {
        const newSegment = {
          ...segment,
          id: `segment-${currentId++}`,
          isNew: true, // Mark as new since we deleted the original
          // dbPartId no longer needed - segments stored as JSON in parts
        };
        
        // Inherit custom_prompt and inputAudioFile from original segment to first text segment only
        // But reset generation status and generated audio since text changed
        if (segment.type === 'text' && !firstTextSegmentProcessed) {
          newSegment.customPrompt = originalSegment.customPrompt;
          newSegment.inputAudioFile = originalSegment.inputAudioFile;
          // Reset generation fields when text changes
          newSegment.generationStatus = 'idle' as const;
          newSegment.generatedAudioFile = null;
          newSegment.generationError = null;
          firstTextSegmentProcessed = true;
        }
        
        return newSegment;
      });
      
      const updatedSegments = [...beforeSegments, ...updatedNewSegments, ...afterSegments];
      console.log(`Final combined segments (${updatedSegments.length}):`, updatedSegments.map(s => ({ id: s.id, type: s.type, content: s.content })));
      // Use dual-save to update both segments and part text
      saveSegmentsAndUpdatePartText(partId, updatedSegments);
    } else {
      // Simple text update without punctuation - reset audio/TTS fields since text changed
      const updatedSegments = currentSegments.map(segment =>
        segment.id === segmentId
          ? { 
              ...segment, 
              content: newText,
              hasChanged: true, // Mark as changed for database update
              isNew: false, // Keep as existing since we're just updating text
              // Reset generation fields when text changes
              generationStatus: 'idle' as const,
              generatedAudioFile: null,
              generationError: null
              // Preserve customPrompt and inputAudioFile
            }
          : segment
      );
      // Use dual-save to update both segments and part text
      saveSegmentsAndUpdatePartText(partId, updatedSegments);
    }
  };

  // Advanced settings handler
  const handleAdvancedSettingChange = (setting: string, value: number) => {
    const newSettings = {
      ...advancedSettings,
      [setting]: value
    };
    
    setAdvancedSettings(newSettings);

    // Update all non-manually-modified pause timings when advanced settings change
    // Batch all updates to avoid race conditions
    const allUpdatedParts: Record<string, TextSegmentData[]> = {};
    let hasChanges = false;
    
    for (const partId in contentData) {
      const updatedSegments = contentData[partId].map(segment => {
        if (segment.type === 'pause' && !segment.isManuallyModified) {
          // Update pause time based on the punctuation and new settings
          let newPauseTime: string;
          
          switch (segment.content) {
            case ',': 
              newPauseTime = `${newSettings.commaPause}s`;
              break;
            case '.': case '!': case '?': 
              newPauseTime = `${newSettings.periodPause}s`;
              break;
            case ':': case ';': 
              newPauseTime = `${newSettings.colonPause}s`;
              break;
            case '-': 
              newPauseTime = `${newSettings.dashPause}s`;
              break;
            case '\n': 
              newPauseTime = `${newSettings.paragraphPause}s`;
              break;
            default: 
              newPauseTime = `${newSettings.commaPause}s`;
              break;
          }
          
          hasChanges = true;
          return { ...segment, pauseTime: newPauseTime };
        }
        return segment;
      });
      
      allUpdatedParts[partId] = updatedSegments;
    }
    
    // Apply all updates in a single batch operation to avoid race conditions
    if (hasChanges) {
      // Update contentData with all changes at once
      setContentData(allUpdatedParts);
      
      // Note: We skip database saves for punctuation timing changes since they're 
      // just UI preferences and will be saved when the user makes other changes
      // This avoids the race condition where multiple saveSegmentsAndUpdatePartText
      // calls would overwrite each other's changes
    }
  };

  // Helper function to calculate total character count for a part
  const getPartCharacterCount = (partId: string): number => {
    const segments = contentData[partId] || [];
    return segments
      .filter(segment => segment.type === 'text')
      .reduce((total, segment) => total + segment.content.length, 0);
  };

  // Helper function to calculate coin cost based on generation status
  const getPartCoinCostByStatus = useCallback((partId: string): number => {
    const segments = contentData[partId] || [];
    const textSegments = segments.filter(segment => segment.type === 'text');
    
    if (textSegments.length === 0) return 0;
    
    const queuedItems = getPartQueueItems(partId);
    const queueCompleted = queuedItems.filter(item => item.status === 'completed').length;
    const savedCompleted = textSegments.filter(s => s.generationStatus === 'completed').length;
    const totalCompleted = Math.max(queueCompleted, savedCompleted);
    const allCompleted = totalCompleted === textSegments.length && textSegments.length > 0;
    
    if (allCompleted) {
      // Re-generate All: count all segments
      return textSegments.reduce((total, segment) => total + segment.content.length, 0);
    } else if (totalCompleted > 0) {
      // Generate Remaining: count only ungenerated segments
      const ungeneratedSegments = textSegments.filter(segment => {
        const queueItem = queuedItems.find(item => item.segmentId === segment.id);
        const isCompleted = queueItem?.status === 'completed' || segment.generationStatus === 'completed';
        return !isCompleted;
      });
      return ungeneratedSegments.reduce((total, segment) => total + segment.content.length, 0);
    } else {
      // Generate: count all segments
      return textSegments.reduce((total, segment) => total + segment.content.length, 0);
    }
  }, [contentData, getPartQueueItems]);

  // Memoize coin costs for all parts to prevent recalculation on every render
  const partCoinCosts = useMemo(() => {
    const costs: Record<string, number> = {};
    selectedParts.forEach(partId => {
      costs[partId] = getPartCoinCostByStatus(partId);
    });
    return costs;
  }, [selectedParts, getPartCoinCostByStatus]);

  const renderSegments = (partId: string) => {
    const segments = contentData[partId] || [];
    
    return segments.map((segment) => {
      if (segment.type === 'pause') {
        const isParagraphBreak = segment.isParagraphBreak || segment.content === '\n';
        return (
          <div key={segment.id} style={{ 
            width: isParagraphBreak ? '100%' : 'auto',
            display: isParagraphBreak ? 'block' : 'inline-block',
            textAlign: isParagraphBreak ? 'center' : 'left',
            margin: isParagraphBreak ? '8px 0' : '2px'
          }}>
            <PauseBubble
              style={{
                backgroundColor: isParagraphBreak ? '#6f42c1' : '#28a745',
                borderColor: isParagraphBreak ? '#5a2d91' : '#20a040'
              }}
            >
              {formatPauseTimeForDisplay(segment.pauseTime || '', segment.isManuallyModified || false, segment.content, segment.id)}
              {isParagraphBreak && ' ¶'}
              <DragHandle
                onMouseDown={(e) => {
                  e.stopPropagation();
                  handleDragStart(partId, segment.id, e);
                }}
              />
            </PauseBubble>
          </div>
        );
      } else {
        const queueStatus = getSegmentStatus(segment.id);
        
        return (
          <TextSegmentWrapper key={segment.id}>
            <TextSegment
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => {
                const newValue = e.target.textContent ?? '';
                handleTextSegmentChange(partId, segment.id, newValue);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
              $hasMenu={true}
              className={
                currentPlayingSegment === segment.id ? 'playing' : 
                queueStatus?.status === 'processing' ? 'generating' : ''
              }
              title={queueStatus?.status === 'error' ? `Generation failed: ${queueStatus.error}` : undefined}
              style={{
                borderLeft: (() => {
                  // Playing state takes highest priority
                  if (currentPlayingSegment === segment.id) {
                    return '3px solid #28a745'; // Green border for currently playing
                  }
                  // Queue status takes priority
                  if (queueStatus) {
                    
                    if (queueStatus.status === 'completed') {  
                      
                      //green not needed, will be handled below
                    //was causing changed segments to indicate audio was generated when it was not
                    //  return '3px solid #28a745'; // Green for completed
                    } else if (queueStatus.status === 'queued') {
                      return '3px solid #ffc107'; // Yellow for queued
                    } else if (queueStatus.status === 'processing') {
                      return '3px solid #17a2b8'; // Cyan for processing
                    } else if (queueStatus.status === 'error') {
                      return '3px solid #dc3545'; // Red for error
                    }
                  }
                  // Then check saved generation status
                  if (segment.generationStatus === 'completed') {
                    console.log('Rendering completed segment:', segment.id, 'in part:', partId, 'with green border', segment);
                    return '3px solid #28a745'; // Green for completed (takes precedence)
                  } else if (segment.inputAudioFile) {
                    return '3px solid #dc3545'; // Red for input audio file
                  } else if (segment.speechSpeed !== undefined && segment.speechSpeed !== advancedSettings.speechSpeed) {
                    return '3px solid #007bff'; // Blue for custom speech speed
                  } else if (segment.customPrompt) {
                    return '3px solid #17a2b8'; // Cyan for custom prompt (deprecated)
                  }
                  return 'none';
                })(),
                paddingLeft: '8px'
              }}
            >
              {segment.content}
            </TextSegment>
            <MenuButton
              onClick={(e) => handleMenuToggle(partId, segment.id, e)}
            >
              <Icon icon={FaEllipsisV} />
            </MenuButton>
            <MenuDropdown $isOpen={openMenuSegmentId === `${partId}:${segment.id}`}>
              <MenuItem 
                onClick={() => handleMenuAction('play', partId, segment.id, segment.content)}
                style={{
                  opacity: (() => {
                    const qStatus = getSegmentStatus(segment.id);
                    return (qStatus?.status === 'completed' && qStatus.audioFile) || segment.generatedAudioFile ? 1 : 0.6;
                  })(),
                  cursor: (() => {
                    const qStatus = getSegmentStatus(segment.id);
                    return (qStatus?.status === 'completed' && qStatus.audioFile) || segment.generatedAudioFile ? 'pointer' : 'not-allowed';
                  })()
                }}
              >
                <Icon icon={FaPlay} />
                {(() => {
                  const qStatus = getSegmentStatus(segment.id);
                  return (qStatus?.status === 'completed' && qStatus.audioFile) || segment.generatedAudioFile ? 'Play' : 'No Audio';
                })()}
              </MenuItem>
              <MenuItem 
                onClick={() => handleMenuAction('playfrom', partId, segment.id, segment.content)}
                style={{
                  opacity: (() => {
                    // Check if this segment and following segments have audio
                    const segmentIndex = contentData[partId]?.findIndex(s => s.id === segment.id) ?? -1;
                    const remainingSegments = contentData[partId]?.slice(segmentIndex) ?? [];
                    const textSegments = remainingSegments.filter(s => s.type === 'text');
                    const hasAllAudio = textSegments.every(s => {
                      const qStatus = getSegmentStatus(s.id);
                      return s.generatedAudioFile || (qStatus?.status === 'completed' && qStatus.audioFile);
                    });
                    return hasAllAudio && textSegments.length > 0 ? 1 : 0.6;
                  })(),
                  cursor: (() => {
                    const segmentIndex = contentData[partId]?.findIndex(s => s.id === segment.id) ?? -1;
                    const remainingSegments = contentData[partId]?.slice(segmentIndex) ?? [];
                    const textSegments = remainingSegments.filter(s => s.type === 'text');
                    const hasAllAudio = textSegments.every(s => {
                      const qStatus = getSegmentStatus(s.id);
                      return s.generatedAudioFile || (qStatus?.status === 'completed' && qStatus.audioFile);
                    });
                    return hasAllAudio && textSegments.length > 0 ? 'pointer' : 'not-allowed';
                  })()
                }}
              >
                <Icon icon={FaForward} />
                Play from here
              </MenuItem>
              <MenuItem 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleMenuAction('generate', partId, segment.id, segment.content);
                }}
                style={{
                  opacity: (() => {
                    const qStatus = getSegmentStatus(segment.id);
                    return (qStatus?.status === 'queued' || qStatus?.status === 'processing') ? 0.6 : 1;
                  })(),
                  cursor: (() => {
                    const qStatus = getSegmentStatus(segment.id);
                    return (qStatus?.status === 'queued' || qStatus?.status === 'processing') ? 'not-allowed' : 'pointer';
                  })()
                }}
              >
                <Icon icon={FaMagic} />
                {(() => {
                  const qStatus = getSegmentStatus(segment.id);
                  if (qStatus) {
                    if (qStatus.status === 'processing') {
                      return 'Processing...';
                    } else if (qStatus.status === 'queued') {
                      return `Queued (${qStatus.queuePosition})`;
                    } else if (qStatus.status === 'completed') {
                      return (<>Re-generate {segment.content.length}<img src="/images/YMcoin.png" alt="coin" style={{width: '14px', height: '14px', marginLeft: '2px', verticalAlign: 'middle'}} /></>);
                    } else if (qStatus.status === 'error') {
                      return (<>Retry {segment.content.length}<img src="/images/YMcoin.png" alt="coin" style={{width: '14px', height: '14px', marginLeft: '2px', verticalAlign: 'middle'}} /></>);
                    }
                  }
                  // Fallback to saved generation status
                  if (segment.generationStatus === 'completed') {
                    return (<>Re-generate {segment.content.length}<img src="/images/YMcoin.png" alt="coin" style={{width: '14px', height: '14px', marginLeft: '2px', verticalAlign: 'middle'}} /></>);
                  } else {
                    return (<>Generate {segment.content.length}<img src="/images/YMcoin.png" alt="coin" style={{width: '14px', height: '14px', marginLeft: '2px', verticalAlign: 'middle'}} /></>);
                  }
                })()}
              </MenuItem>
              <MenuItem 
                onClick={() => handleMenuAction('reuse', partId, segment.id, segment.content)}
                title="Re-use existing cached audio"
              >
                <Icon icon={FaMicrophone} />
                Re-use Speech
              </MenuItem>
              <MenuItem 
                onClick={() => handleMenuAction('speed', partId, segment.id, segment.content)}
                $isUnderlined={segment.speechSpeed !== undefined && segment.speechSpeed !== advancedSettings.speechSpeed}
                $underlineColor={segment.speechSpeed !== undefined && segment.speechSpeed !== advancedSettings.speechSpeed ? '#007bff' : undefined}
              >
                <Icon icon={FaCog} />
                Speech Speed
              </MenuItem>
            </MenuDropdown>
          </TextSegmentWrapper>
        );
      }
    });
  };

  if (selectedParts.length === 0) {
    return (
      <Container>
        <EmptyState>
          No parts selected. Please go back to the Plan page to select meditation parts.
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <div style={{ color: '#cccccc', fontSize: '16px' }}>
         
        </div>
        <HeaderControls>
          <Select 
            value={selectedVoice} 
            onChange={(e) => {
              const voiceId = e.target.value;
              handleVoiceChange(voiceId);
              // Play sample audio when voice is changed
              // if (voiceId && voiceId !== 'default') {
              //   playSampleAudio(voiceId);
              // }
            }}
            style={{
              borderColor: !selectedVoice ? '#dc3545' : '#444',
              borderWidth: !selectedVoice ? '2px' : '1px'
            }}
          >
            <option value="">{!selectedVoice ? '⚠️ Select Voice (Required)' : 'Select Voice'}</option>
            {availableVoices.map(voice => (
              <option key={voice.id} value={voice.id}>
                {voice.name} {voice.isPrivate ? '(Private)' : ''}
              </option>
            ))}
          </Select>
          
          {selectedVoiceData && (
            
      
              <PlaySampleButton
                onClick={() => playSampleAudio(selectedVoiceData.id)}
                disabled={!selectedVoiceData.sampleAudio}
                title="Play sample audio"
              >
                <Icon icon={FaPlay} />
              </PlaySampleButton>
            
          )}
          
          <Select value={selectedLanguage} onChange={(e) => console.log('Language change not implemented:', e.target.value)}>
            <option value="en">English</option>
          </Select>
          
          <GearButton onClick={() => setShowAdvancedModal(true)} title="Advanced Settings">
            <Icon icon={FaCog} />
          </GearButton>
        </HeaderControls>
      </Header>

      {/* Queue Status Bar */}
      <QueueStatusBar>
        <QueueInfo>
          <QueueStat>
            <QueueDot $color={isConnected ? '#28a745' : '#dc3545'} />
            <ConnectionStatus $connected={isConnected}>
              {isConnected ? 'Connected' : 'Disconnected'}
            </ConnectionStatus>
          </QueueStat>
          <QueueStat>
            <QueueDot $color="#17a2b8" />
            Global Queue: {globalQueueLength}
          </QueueStat>
          <QueueStat>
            <QueueDot $color="#ffc107" />
            Your Items: {queueItems.filter(item => item.status === 'queued' || item.status === 'processing').length}
          </QueueStat>
        </QueueInfo>
      </QueueStatusBar>

      <ContentSection>
        {selectedParts.map((partId, index) => {
          const part = meditationParts.find(p => p.id === partId);
          if (!part) return null;
          
          const canMoveUp = index > 0;
          const canMoveDown = index < selectedParts.length - 1;
          const isCollapsed = collapsedParts.has(partId);
          const hasContent = editingContent[partId] || contentData[partId]?.length > 0;
          
          return (
            <ContentBox key={partId}>
              <ContentHeader>
                <ContentTitleWithControls>
                  <ReorderControls>
                    <ReorderButton
                      onClick={() => movePartUp(partId)}
                      disabled={!canMoveUp}
                      title="Move up"
                    >
                      <Icon icon={FaChevronUp} />
                    </ReorderButton>
                    <ReorderButton
                      onClick={() => movePartDown(partId)}
                      disabled={!canMoveDown}
                      title="Move down"
                    >
                      <Icon icon={FaChevronDown} />
                    </ReorderButton>
                  </ReorderControls>
                  <ContentTitleInput
                    value={getPartDisplayName(partId, part.title)}
                    onChange={(e) => handlePartNameChange(partId, e.target.value)}
                    placeholder={part.title}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.currentTarget.blur();
                      }
                    }}
                  />
                </ContentTitleWithControls>
                <PartActionButtons>
                  <GenerateButton
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (!selectedVoiceData) {
                        setShowNoVoiceModal(true);
                        return;
                      }
                      handleMenuAction('generate', partId, '', '');
                    }}
                    disabled={getPartCharacterCount(partId) === 0}
                    title="Generate speech for entire part"
                  >
                    <Icon icon={FaMagic} />
                    {(() => {
                      const textSegments = contentData[partId]?.filter(s => s.type === 'text') || [];
                      const queuedItems = getPartQueueItems(partId);
                      const queuedOrProcessing = queuedItems.filter(item => 
                        item.status === 'queued' || item.status === 'processing'
                      ).length;
                      const savedCompleted = textSegments.filter(s => s.generationStatus === 'completed').length;
                      // Use saved completed count as it gets updated by the queue completion callback
                      const totalCompleted = savedCompleted;
                      
                      if (queuedOrProcessing > 0) {
                        return `Processing... (${totalCompleted}/${textSegments.length})`;
                      } else if (totalCompleted === textSegments.length && textSegments.length > 0) {
                        return (<>Re-generate All {partCoinCosts[partId] || 0}<img src="/images/YMcoin.png" alt="coin" style={{width: '14px', height: '14px', marginLeft: '2px', verticalAlign: 'middle'}} /></>);
                      } else if (totalCompleted > 0) {
                        return (<>Generate Remaining {partCoinCosts[partId] || 0}<img src="/images/YMcoin.png" alt="coin" style={{width: '14px', height: '14px', marginLeft: '2px', verticalAlign: 'middle'}} /></>);
                      } else {
                        return (<>Generate {partCoinCosts[partId] || 0}<img src="/images/YMcoin.png" alt="coin" style={{width: '14px', height: '14px', marginLeft: '2px', verticalAlign: 'middle'}} /></>);
                      }
                    })()}
                  </GenerateButton>
                  <ReuseSpeechButton
                    onClick={() => handleMenuAction('reuse', partId, '', '')}
                    disabled={getPartCharacterCount(partId) === 0}
                    title="Re-use existing speech"
                  >
                    <Icon icon={FaMicrophone} />
                    Re-use Speech
                  </ReuseSpeechButton>
                  {isPlayingAudio && currentPlayingPart === partId ? (
                    <StopButton
                      onClick={() => handleMenuAction('stop', partId, '', '')}
                      title="Stop part audio"
                    >
                      <Icon icon={FaStop} />
                      Stop
                    </StopButton>
                  ) : (
                    <PlayButton
                      onClick={() => handleMenuAction('play', partId, '', '')}
                      disabled={getPartCharacterCount(partId) === 0}
                      title="Play part audio"
                    >
                      <Icon icon={FaPlay} />
                      Play
                    </PlayButton>
                  )}
                </PartActionButtons>
                <CollapseButton
                  onClick={() => togglePartCollapse(partId)}
                  title={isCollapsed ? 'Expand' : 'Collapse'}
                >
                  {isCollapsed ? '+' : '−'}
                </CollapseButton>
              </ContentHeader>
              
              {!isCollapsed && (
                hasContent ? (
                  <SegmentDisplay>
                    {renderSegments(partId)}
                  </SegmentDisplay>
                ) : (
                  <PlaceholderText>
                    No content available. Please add content in the Write step.
                  </PlaceholderText>
                )
              )}
            </ContentBox>
          );
        })}
      </ContentSection>

      <ButtonContainer>
      <FinalizeButton 
            onClick={() => setShowFinalizeModal(true)}
            disabled={isFinalizingAudio}
            title="Combine all segments into a single audio file"
          >
            <Icon icon={FaCompactDisc} />
            {isFinalizingAudio ? 'Processing...' : 'Finalize Audio'}
          </FinalizeButton>
      </ButtonContainer>

      <HelpButton
        title="Create Your Meditation - Speech"
        content={helpContent}
        position="fixed-bottom-right"
        />

      {/* Advanced Settings Modal */}
      <Modal $isOpen={showAdvancedModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Advanced Settings</ModalTitle>
            <CloseButton onClick={() => setShowAdvancedModal(false)}>×</CloseButton>
          </ModalHeader>
          
          <SettingGroup>
            <SettingLabel>Speech Speed</SettingLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SettingInput 
                type="range" 
                min="0.6" 
                max="1.2" 
                step="0.1" 
                value={advancedSettings.speechSpeed}
                onChange={(e) => setAdvancedSettings({ ...advancedSettings, speechSpeed: Number(e.target.value) })} 
              />
              <span style={{ color: '#ffffff', fontSize: '14px', minWidth: '40px' }}>
                {advancedSettings.speechSpeed.toFixed(1)}x
              </span>
            </div>
          </SettingGroup>
          
          <SettingGroup>
            <SettingLabel>Comma Pause (s)</SettingLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SettingInput 
                type="range" 
                min="0" 
                max="4" 
                step="0.1" 
                value={advancedSettings.commaPause}
                onChange={(e) => handleAdvancedSettingChange('commaPause', Number(e.target.value))} 
              />
              <span style={{ color: '#ffffff', fontSize: '14px', minWidth: '40px' }}>
                {advancedSettings.commaPause.toFixed(1)}s
              </span>
            </div>
          </SettingGroup>
          
          <SettingGroup>
            <SettingLabel>Period Pause (s)</SettingLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SettingInput 
                type="range" 
                min="0" 
                max="4" 
                step="0.1" 
                value={advancedSettings.periodPause}
                onChange={(e) => handleAdvancedSettingChange('periodPause', Number(e.target.value))} 
              />
              <span style={{ color: '#ffffff', fontSize: '14px', minWidth: '40px' }}>
                {advancedSettings.periodPause.toFixed(1)}s
              </span>
            </div>
          </SettingGroup>
          
          <SettingGroup>
            <SettingLabel>Colon/Semicolon Pause (s)</SettingLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SettingInput 
                type="range" 
                min="0" 
                max="4" 
                step="0.1" 
                value={advancedSettings.colonPause}
                onChange={(e) => handleAdvancedSettingChange('colonPause', Number(e.target.value))} 
              />
              <span style={{ color: '#ffffff', fontSize: '14px', minWidth: '40px' }}>
                {advancedSettings.colonPause.toFixed(1)}s
              </span>
            </div>
          </SettingGroup>
          
          <SettingGroup>
            <SettingLabel>Dash Pause (s)</SettingLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SettingInput 
                type="range" 
                min="0" 
                max="4" 
                step="0.1" 
                value={advancedSettings.dashPause}
                onChange={(e) => handleAdvancedSettingChange('dashPause', Number(e.target.value))} 
              />
              <span style={{ color: '#ffffff', fontSize: '14px', minWidth: '40px' }}>
                {advancedSettings.dashPause.toFixed(1)}s
              </span>
            </div>
          </SettingGroup>
          
          <SettingGroup>
            <SettingLabel>Paragraph Pause (s)</SettingLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SettingInput 
                type="range" 
                min="0" 
                max="4" 
                step="0.1" 
                value={advancedSettings.paragraphPause}
                onChange={(e) => handleAdvancedSettingChange('paragraphPause', Number(e.target.value))} 
              />
              <span style={{ color: '#ffffff', fontSize: '14px', minWidth: '40px' }}>
                {advancedSettings.paragraphPause.toFixed(1)}s
              </span>
            </div>
          </SettingGroup>
        </ModalContent>
      </Modal>

      {/* Custom Prompt Modal */}
      <Modal $isOpen={showCustomPromptModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Custom Prompt</ModalTitle>
            <CloseButton onClick={closeCustomPromptModal}>×</CloseButton>
          </ModalHeader>
          
          <SettingGroup>
            <SettingLabel>Custom Prompt for Text Segment</SettingLabel>
            <SettingTextArea
              value={customPromptValue}
              onChange={(e) => setCustomPromptValue(e.target.value)}
              placeholder="Enter custom prompt for this text segment..."
              style={{
                minHeight: '120px'
              }}
            />
          </SettingGroup>
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button
              onClick={closeCustomPromptModal}
              style={{
                background: 'none',
                border: '1px solid #444',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveCustomPrompt}
              style={{
                backgroundColor: '#007bff',
                border: 'none',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Save
            </button>
          </div>
        </ModalContent>
      </Modal>

      {/* Speech Speed Modal */}
      <Modal $isOpen={showSpeechSpeedModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Speech Speed</ModalTitle>
            <CloseButton onClick={closeSpeechSpeedModal}>×</CloseButton>
          </ModalHeader>
          
          <SettingGroup>
            <SettingLabel>Speech Speed for Text Segment</SettingLabel>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <SettingInput 
                type="range" 
                min="0.6" 
                max="1.2" 
                step="0.1" 
                value={speechSpeedValue}
                onChange={(e) => setSpeechSpeedValue(Number(e.target.value))} 
              />
              <span style={{ color: '#ffffff', fontSize: '14px', minWidth: '40px' }}>
                {speechSpeedValue.toFixed(1)}x
              </span>
            </div>
            <div style={{ 
              fontSize: '12px', 
              color: '#ccc', 
              marginTop: '8px',
              lineHeight: '1.4'
            }}>
              Default speed is {advancedSettings.speechSpeed.toFixed(1)}x. 
              This setting will override the default for this segment only.
            </div>
          </SettingGroup>
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button
              onClick={closeSpeechSpeedModal}
              style={{
                background: 'none',
                border: '1px solid #444',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSaveSpeechSpeed}
              style={{
                backgroundColor: '#28a745',
                border: 'none',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600'
              }}
            >
              Save
            </button>
          </div>
        </ModalContent>
      </Modal>

      {/* Voice Recording Modal */}
      <Modal $isOpen={showVoiceModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Voice Recording</ModalTitle>
            <CloseButton onClick={closeVoiceModal}>×</CloseButton>
          </ModalHeader>
          
          <SettingGroup>
            <SettingLabel>Text to Read</SettingLabel>
            <div style={{
              padding: '12px',
              border: '1px solid #444',
              borderRadius: '6px',
              backgroundColor: '#333',
              color: '#ffffff',
              fontSize: '14px',
              lineHeight: '1.5',
              minHeight: '80px'
            }}>
              {voiceModalText}
            </div>
          </SettingGroup>

          <SettingGroup>
            <div style={{ 
              padding: '12px',
              backgroundColor: '#2d3436',
              borderRadius: '8px',
              marginBottom: '15px',
              fontSize: '14px',
              color: '#74b9ff',
              fontStyle: 'italic'
            }}>
              Record yourself reading the text with the tone and pace you want the generated voice to emulate.
            </div>
          </SettingGroup>

          <SettingGroup>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '15px' }}>
              {!isRecording && !recordedAudio && (
                <button
                  onClick={startRecording}
                  style={{
                    backgroundColor: '#28a745',
                    border: 'none',
                    color: '#ffffff',
                    padding: '12px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Icon icon={FaMicrophone} />
                  Start Recording
                </button>
              )}
              
              {isRecording && (
                <button
                  onClick={stopRecording}
                  style={{
                    backgroundColor: '#dc3545',
                    border: 'none',
                    color: '#ffffff',
                    padding: '12px 20px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  ⏹ Stop Recording
                </button>
              )}
              
              {recordedAudio && (
                <>
                  <button
                    onClick={playRecording}
                    style={{
                      backgroundColor: '#007bff',
                      border: 'none',
                      color: '#ffffff',
                      padding: '12px 20px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    <Icon icon={FaPlay} />
                    Play
                  </button>
                  <button
                    onClick={removeRecording}
                    style={{
                      backgroundColor: '#6c757d',
                      border: 'none',
                      color: '#ffffff',
                      padding: '12px 20px',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Re-record
                  </button>
                </>
              )}
            </div>
            
            {isRecording && (
              <div style={{
                color: '#dc3545',
                fontSize: '14px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🔴 Recording...
              </div>
            )}
          </SettingGroup>
          
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button
              onClick={closeVoiceModal}
              disabled={isSavingAudio}
              style={{
                background: 'none',
                border: '1px solid #444',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: isSavingAudio ? 'not-allowed' : 'pointer',
                opacity: isSavingAudio ? 0.6 : 1
              }}
            >
              Cancel
            </button>
            
            {/* Show Remove button only if segment has existing audio */}
            {contentData[voiceModalPartId]?.find(s => s.id === voiceModalSegmentId)?.inputAudioFile && (
              <button
                onClick={removeVoiceAudio}
                disabled={isSavingAudio}
                style={{
                  backgroundColor: '#dc3545',
                  border: 'none',
                  color: '#ffffff',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  cursor: isSavingAudio ? 'not-allowed' : 'pointer',
                  fontWeight: '600',
                  opacity: isSavingAudio ? 0.6 : 1
                }}
              >
                {isSavingAudio ? 'Removing...' : 'Remove'}
              </button>
            )}
            
            <button
              onClick={acceptRecording}
              disabled={!recordedAudio || isSavingAudio}
              style={{
                backgroundColor: (!recordedAudio || isSavingAudio) ? '#666' : '#28a745',
                border: 'none',
                color: '#ffffff',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: (!recordedAudio || isSavingAudio) ? 'not-allowed' : 'pointer',
                fontWeight: '600'
              }}
            >
              {isSavingAudio ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </ModalContent>
      </Modal>

      {/* No Voice Selected Modal */}
      <Modal $isOpen={showNoVoiceModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>No Voice Selected</ModalTitle>
            <CloseButton onClick={() => setShowNoVoiceModal(false)}>×</CloseButton>
          </ModalHeader>
          
          <div style={{ 
            padding: '20px 0',
            color: '#ffffff',
            fontSize: '16px',
            lineHeight: '1.5'
          }}>
            <p style={{ margin: '0 0 15px 0' }}>
              Please select a voice before generating speech. You can choose from the available voices in the dropdown menu at the top of the page.
            </p>
            <p style={{ margin: '0 0 15px 0', color: '#cccccc', fontSize: '14px' }}>
              The voice selection determines how your meditation script will sound when converted to audio.
            </p>
            <div style={{
              backgroundColor: '#1a1a1a',
              border: '1px solid #444',
              borderRadius: '6px',
              padding: '12px',
              fontSize: '14px',
              color: '#ffc107'
            }}>
              💡 <strong>Tip:</strong> You can play a sample of each voice by selecting it and clicking the play button.
            </div>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button
              onClick={() => setShowNoVoiceModal(false)}
              style={{
                backgroundColor: '#007bff',
                border: 'none',
                color: '#ffffff',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Got it
            </button>
          </div>
        </ModalContent>
      </Modal>

      {/* Insufficient Coins Modal */}
      <Modal $isOpen={showInsufficientCoinsModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Insufficient Coins</ModalTitle>
            <CloseButton onClick={() => setShowInsufficientCoinsModal(false)}>×</CloseButton>
          </ModalHeader>
          
          <div style={{ 
            padding: '20px 0',
            color: '#ffffff',
            fontSize: '16px',
            lineHeight: '1.5'
          }}>
            <p style={{ margin: '0 0 15px 0' }}>
              You don't have enough coins to generate speech for {coinErrorDetails?.context || 'this content'}.
            </p>
            
            {coinErrorDetails && (
              <div style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #444',
                borderRadius: '6px',
                padding: '15px',
                margin: '15px 0',
                fontSize: '14px'
              }}>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#cccccc' }}>Required:</span> <span style={{ color: '#ffc107', fontWeight: '600' }}>{coinService.formatCoinAmount(coinErrorDetails.required)} coins</span>
                </div>
                <div style={{ marginBottom: '8px' }}>
                  <span style={{ color: '#cccccc' }}>Available:</span> <span style={{ color: user?.coins && user.coins >= coinErrorDetails.required ? '#28a745' : '#dc3545', fontWeight: '600' }}>{coinService.formatCoinAmount(coinErrorDetails.available)} coins</span>
                </div>
                <div>
                  <span style={{ color: '#cccccc' }}>Shortfall:</span> <span style={{ color: '#dc3545', fontWeight: '600' }}>{coinService.formatCoinAmount(coinErrorDetails.shortfall)} coins</span>
                </div>
              </div>
            )}
            
            <p style={{ margin: '15px 0 0 0', color: '#cccccc', fontSize: '14px' }}>
              You can purchase more coins in your account settings or try generating a smaller amount of text.
            </p>
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <button
              onClick={() => setShowInsufficientCoinsModal(false)}
              style={{
                backgroundColor: '#6c757d',
                border: 'none',
                color: '#ffffff',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShowInsufficientCoinsModal(false);
                // TODO: Navigate to account/billing page
                console.log('Navigate to coin purchase page');
              }}
              style={{
                backgroundColor: '#ffc107',
                border: 'none',
                color: '#000000',
                padding: '10px 20px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px'
              }}
            >
              Get More Coins
            </button>
          </div>
        </ModalContent>
      </Modal>

      {/* Finalize Audio Modal */}
      <Modal $isOpen={showFinalizeModal}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>
              <Icon icon={FaCompactDisc} />
              Finalize Script Audio
            </ModalTitle>
            <CloseButton onClick={() => setShowFinalizeModal(false)}>
              <Icon icon={FaTimes} />
            </CloseButton>
          </ModalHeader>
          
          <ModalBody>
            {!isFinalizingAudio && !finalizedAudioFile && !finalizationError && (
              <>
                <p>This will combine all segments and pauses into a single audio file for further editing.</p>
                <p><strong>Make sure all text segments have generated audio, and you are happy with pause timings, before finalizing.</strong></p>
              </>
            )}
            
            {isFinalizingAudio && (
              <ProgressContainer>
                <ProgressText>
                  <Icon icon={FaCompactDisc} />
                  Processing audio segments...
                </ProgressText>
                <p>This may take a few moments depending on the length of your script.</p>
              </ProgressContainer>
            )}
            
            {finalizedAudioFile && !finalizationError && (
              <SuccessMessage>
                <Icon icon={FaCompactDisc} />
                Audio finalization completed successfully!
              </SuccessMessage>
            )}
            
            {finalizationError && (
              <ErrorMessage>
                <strong>Finalization Failed:</strong><br />
                {finalizationError}
              </ErrorMessage>
            )}
          </ModalBody>
          
          <ModalActions>
            {finalizedAudioFile && !finalizationError && (
               //<>
              //   <PlayFinalButton
              //     onClick={() => {
              //       const audio = new Audio(`${config.apiUrl}${finalizedAudioFile}`);
              //       audio.play().catch(error => {
              //         console.error('Error playing finalized audio:', error);
              //       });
              //     }}
              //   >
              //     <Icon icon={FaPlay} />
              //     Play Final Audio
              //   </PlayFinalButton>
                <ContinueButton
                  onClick={() => {
                    setShowFinalizeModal(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });//scroll to top of page, since page just loaded
                    navigate(isEditingExisting ? `/edit/${scriptId}/audio` : '/create/audio');
                  }}
                >
                  <Icon icon={FaArrowRight} />
                  Continue to Audio
                </ContinueButton>
              //</>
            )}
            
            {!finalizedAudioFile && !isFinalizingAudio && (
              <FinalizeButton
                onClick={async () => {
                  await finalizeScriptAudio();
                }}
                disabled={isFinalizingAudio}
              >
                <Icon icon={FaCompactDisc} />
                Start Finalization
              </FinalizeButton>
            )}
            
            {!finalizedAudioFile && (
              <button
                onClick={() => setShowFinalizeModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  border: 'none',
                  color: '#ffffff',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
            )}
          </ModalActions>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default Speech; 